<template>
  <div class="details-container">
    <audience-insights-summary
      :average-audience-concentration="averageAudienceConcentration"
      :no-audience-units-count="noAudienceUnitCount"
      :units-count="totalUnitCount"
      @update-unit-i-ds="() => emit('updateUnitIDs')"
      @download-units="state.isDownloadModalVisible = true" />
    <div class="row">
      <div class="col-xl-7">
        <div class="row">
          <div class="col-12">
            <div class="pane-header">
              <span class="icon"><icon-colored-search /></span>
              <span class="text">{{ capitalize($t('messages.averageAudienceConcentrationPerScreen')) }}</span>
            </div>
          </div>
          <div class="col-12">
            <div class="pane-content pad-top notes-container">
              <div class="row">
                <div class="col-12">
                  <client-side-table
                    id="units_audience_concentration_table"
                    ref="unitsAudienceConcentrationTable"
                    category="Audience Concentration > Units Audience Concentration Table"
                    :pagination="true"
                    :items="unitConcentrationData"
                    :columns="columns"
                    :show-filters="true"
                    :enable-download="true"
                    :handle-download="true"
                    first-column-size="xs">
                    <template #externalID="{ item }: { item: UnitConcentrationData }">
                      <a
                        href="#"
                        @click.prevent="unitClicked(item.id)">
                        {{ item.externalID }}
                      </a>
                      <span
                        v-if="item.unitID === state.selectedUnit"
                        v-tooltip="capitalize($t('messages.selectedScreen'))"
                        class="selected-unit">
                        <icon-check2 />
                      </span>
                    </template>
                    <template #averageConcentrations="{ item }: { item: UnitConcentrationData }">
                      {{ getAverageConcentrationFormatted(item.averageConcentrations) }}
                    </template>
                    <template #mediaOwner="{ item }: { item: UnitConcentrationData }">
                      <MediaOwners :ids="[item.mediaOwnerID]" />
                    </template>
                  </client-side-table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-xl-5">
        <!-- RESULTS-->
        <CardTemplate
          :icon="IconColoredImpression"
          :name="audienceConcentrationCardName">
          <template #content>
            <div>
              <span
                >{{ capitalize($t('messages.selectedScreen')) }}: <b>{{ state.selectedUnit }}</b></span
              >
            </div>
            <UnitAudienceData
              v-if="state.audienceData?.length"
              :audience-data="state.audienceData"
              :start-date="state.audienceDataFromDate"
              :chart-tooltip-name="capitalize($t('names.audienceConcentration', { n: 2 }))"
              audience-data-format="0.00%"
              @previous-week="getAudienceDataForPreviousWeek"
              @next-week="getAudienceDataForNextWeek" />

            <div
              v-else
              class="alert alert-info icon-sm">
              <IconInfo />
              <span>
                {{ $t('messages.noAudienceConcentrationDataFoundForSearch') }}
              </span>
            </div>
          </template>
        </CardTemplate>
      </div>
    </div>
  </div>
  <modal
    :visible="state.isDownloadModalVisible"
    :title="capitalize($t('actions.download'))"
    :body="capitalize($t('messages.chooseAFileFormat'))"
    button1-class="default"
    :button1-caption="capitalize($t('actions.cancel'))"
    :button2-visible="false"
    @confirmed="state.isDownloadModalVisible = false"
    @cancelled="state.isDownloadModalVisible = false"
    >>
    <template #modal-after-form>
      <div class="flex-v-full">
        <div class="file-types">
          <div class="file-type">
            <a
              data-action-type="download-csv-file"
              @click="downloadCSV">
              <icon-file-c-s-v />
            </a>
          </div>

          <div class="file-type">
            <a
              data-action-type="download-xlsx-file"
              @click="downloadXLSX">
              <icon-file-x-l-s />
            </a>
          </div>
        </div>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
  import { DateTime } from 'luxon';
  import { capitalize, computed, onMounted, reactive } from 'vue';

  import Modal from '@/common/components/controls/Modal.vue';
  import ClientSideTable from '@/common/components/controls/table/ClientSideTable.vue';
  import CardTemplate from '@/common/components/templates/CardTemplate.vue';
  import CellType from '@/common/enums/cell-type.enum';
  import { IconColoredImpression, IconColoredSearch } from '@/common/icons/colored';
  import { IconCheck2, IconFileCSV, IconFileXLS, IconInfo } from '@/common/icons/monochrome';
  import {
    AudienceConcentrationPerUnit,
    AudienceConcentrationPerUnitData,
  } from '@/common/models/audience/AudienceConcentrationPerUnit';
  import NumericFilterComparisonOptions from '@/common/models/query-params/NumericFilterComparisonOptions';
  import { toServerDate } from '@/globals';
  import AudienceInsightsSummary from '@/org-app/components/audienceConcentration/AudienceInsightsSummary.vue';
  import MediaOwners from '@/org-app/components/mediaOwners/MediaOwnersList.vue';
  import UnitAudienceData from '@/org-app/components/units/UnitAudienceData.vue';
  import DownloadUtils from '@/utils/DownloadUtils';
  import formatNumber from '@/utils/NumberFormatter';
  import { mapOrgAppActions } from '@/utils/StateMapping';
  import i18n from '@/vueI18n';

  // Types
  interface Props {
    units: AudienceConcentrationPerUnitData[];
    invalidUnits: AudienceConcentrationPerUnitData[];
    demographicIDs: number[];
    averageAudienceConcentration: string;
  }
  interface State {
    audienceData: number[];
    audienceDataFromDate: DateTime;
    audienceDataToDate: DateTime;
    inputTimeout: any;
    selectedUnit: number;
    isDownloadModalVisible: boolean;
  }

  // Setup
  const emit = defineEmits<{
    (e: 'updateUnitIDs');
  }>();
  const props = defineProps<Props>();
  const { getUnitsAudienceData } = mapOrgAppActions();

  // State
  const state = reactive<State>({
    audienceData: null,
    audienceDataFromDate: DateTime.local().startOf('week'),
    audienceDataToDate: DateTime.local().endOf('week').startOf('day'),
    inputTimeout: null,
    selectedUnit: null,
    isDownloadModalVisible: false,
  });
  const unitConcentrationData = computed(() => {
    return props.units.map((data) => {
      return {
        id: data.unitID,
        unitID: data.unitID,
        externalID: data.externalID,
        siteID: data.siteID,
        siteName: data.siteName,
        cityName: data.cityName,
        averageConcentrations: data.averageConcentrations,
        mediaTypeName: data.mediaType?.name,
        mediaTypeID: data.mediaType?.mediatypeID,
        parentTypeID: data.mediaType?.parentMediatypeID,
        mediaOwnerID: data.ownerID,
      };
    });
  });
  type UnitConcentrationData = (typeof unitConcentrationData.value)[number];

  //
  // Computed
  //
  const audienceConcentrationCardName = capitalize(i18n.global.t('names.audienceConcentration', { n: 2 }));
  const columns = computed(() => ({
    externalID: AudienceConcentrationPerUnit.entityProperties.externalID.getTableColumn({ allowSort: false }),
    siteID: AudienceConcentrationPerUnit.entityProperties.siteID.getTableColumn({ allowSort: false }),
    siteName: AudienceConcentrationPerUnit.entityProperties.siteName.getTableColumn({ allowSort: false }),
    cityName: AudienceConcentrationPerUnit.entityProperties.cityName.getTableColumn({ allowSort: false }),
    averageConcentrations: AudienceConcentrationPerUnit.entityProperties.averageConcentrations.getTableColumn({
      numericalOperatorsToExclude: [
        NumericFilterComparisonOptions.Equals.id,
        NumericFilterComparisonOptions.NotEquals.id,
        NumericFilterComparisonOptions.LessThan.id,
        NumericFilterComparisonOptions.GreaterThan.id,
        NumericFilterComparisonOptions.IsEmpty.id,
      ],
    }),
    mediaTypeName: AudienceConcentrationPerUnit.entityProperties.mediaTypeName.getTableColumn({ allowSort: false }),
    mediaOwner: AudienceConcentrationPerUnit.entityProperties.mediaOwner.getTableColumn({ allowSort: false }),
  }));
  const totalUnitCount = computed(() => props.units.length);
  const noAudienceUnitCount = computed(() => props.invalidUnits.length);

  //
  // Methods
  //

  // Data fetching
  async function getAudienceData(): Promise<void> {
    if (!state.selectedUnit) return;

    const payload = {
      ids: [state.selectedUnit],
      demographicIDs: props.demographicIDs,
      fromDate: toServerDate(state.audienceDataFromDate),
      toDate: toServerDate(state.audienceDataToDate),
    };

    const res = await getUnitsAudienceData(payload);

    const selectedDemographicConcentrations = res[state.selectedUnit];
    const demographicIDsKey = payload.demographicIDs.sort((a, b) => a - b).join(',');
    const demographicConcentration = selectedDemographicConcentrations[demographicIDsKey];
    state.audienceData = demographicConcentration?.length ? demographicConcentration : [];
  }

  // Helpers
  function getAverageConcentrationFormatted(averageConcentration: number) {
    return formatNumber(averageConcentration, '0.00%');
  }
  async function unitClicked(unitID: number): Promise<void> {
    state.selectedUnit = unitID;
    await getAudienceData();
  }
  async function getAudienceDataForNextWeek(): Promise<void> {
    state.audienceDataFromDate = state.audienceDataFromDate.plus({ days: 7 });
    state.audienceDataToDate = state.audienceDataToDate.plus({ days: 7 });
    await getAudienceData();
  }
  async function getAudienceDataForPreviousWeek(): Promise<void> {
    state.audienceDataFromDate = state.audienceDataFromDate.minus({ days: 7 });
    state.audienceDataToDate = state.audienceDataToDate.minus({ days: 7 });
    await getAudienceData();
  }

  // Download
  async function downloadCSV() {
    const data = props.invalidUnits.map((u) => [u.externalID]);
    DownloadUtils.downloadAsCsv('Screens Without Concentration', data, [CellType.STRING], ['Screen ID']);
    state.isDownloadModalVisible = false;
  }
  async function downloadXLSX() {
    const data = props.invalidUnits.map((u) => [u.externalID]);
    DownloadUtils.downloadAsXlsx('Screens Without Concentration', data, [CellType.STRING], ['Screen ID']);
    state.isDownloadModalVisible = false;
  }

  //
  // Mounted
  //
  onMounted(async () => {
    state.audienceDataFromDate = DateTime.local().startOf('week');
    state.audienceDataToDate = DateTime.local().endOf('week').startOf('day');
  });
</script>

<style scoped lang="scss">
  .pane-header {
    .icon-info {
      svg {
        height: 1.6rem;
        width: 1.6rem;
        stroke: var(--secondaryColor);
      }
    }
  }
  .pane-content {
    .selected-unit {
      display: flex;

      svg {
        margin-left: 1rem;
        height: 2rem;
        fill: var(--iconColor);
      }
    }
  }
</style>
